import { useMemo } from 'react';
import {
    getCurrentPlan,
    hasFeature,
    getLimit,
    hasReachedLimit,
    isLanguageSupported,
    getUpgradeMessage,
    PLANS
} from '../utils/planFeatures';

/**
 * Custom hook to access plan features and limits
 * @param {Object} companyProfile - Company profile object
 * @returns {Object} Plan features and utility functions
 */
export const usePlanFeatures = (companyProfile) => {
    const currentPlan = useMemo(() => {
        return getCurrentPlan(companyProfile);
    }, [companyProfile]);

    const isPremium = currentPlan === PLANS.PREMIUM;
    const isStarter = currentPlan === PLANS.STARTER;
    const isTrial = currentPlan === PLANS.TRIAL;

    const features = useMemo(() => ({
        // Core
        menu: hasFeature(companyProfile, 'menu'),
        tables: hasFeature(companyProfile, 'tables'),
        orders: hasFeature(companyProfile, 'orders'),
        kitchen: hasFeature(companyProfile, 'kitchen'),
        payments: hasFeature(companyProfile, 'payments'),
        customerPage: hasFeature(companyProfile, 'customerPage'),
        selfService: hasFeature(companyProfile, 'selfService'),

        // Analytics
        basicAnalytics: hasFeature(companyProfile, 'basicAnalytics'),
        advancedAnalytics: hasFeature(companyProfile, 'advancedAnalytics'),
        downloadReports: hasFeature(companyProfile, 'downloadReports'),

        // Inventory
        ingredients: hasFeature(companyProfile, 'ingredients'),
        lowStockAlerts: hasFeature(companyProfile, 'lowStockAlerts'),
        costManagement: hasFeature(companyProfile, 'costManagement'),

        // Staff & HR
        staffManagement: hasFeature(companyProfile, 'staffManagement'),
        attendance: hasFeature(companyProfile, 'attendance'),
        leaveManagement: hasFeature(companyProfile, 'leaveManagement'),
        salaryReports: hasFeature(companyProfile, 'salaryReports'),

        // Advanced
        aiNutrition: hasFeature(companyProfile, 'aiNutrition'),
        bulkUpload: hasFeature(companyProfile, 'bulkUpload'),
        multiBranch: hasFeature(companyProfile, 'multiBranch'),
        customRoles: hasFeature(companyProfile, 'customRoles'),
        prioritySupport: hasFeature(companyProfile, 'prioritySupport'),
    }), [companyProfile]);

    const limits = useMemo(() => ({
        menuItems: getLimit(companyProfile, 'menuItems'),
        tables: getLimit(companyProfile, 'tables'),
        branches: getLimit(companyProfile, 'branches'),
        languages: getLimit(companyProfile, 'languages'),
    }), [companyProfile]);

    const canAddMenuItem = (currentCount) => {
        return !hasReachedLimit(companyProfile, 'menuItems', currentCount);
    };

    const canAddTable = (currentCount) => {
        return !hasReachedLimit(companyProfile, 'tables', currentCount);
    };

    const canAddBranch = (currentCount) => {
        return !hasReachedLimit(companyProfile, 'branches', currentCount);
    };

    const canUseLanguage = (languageCode) => {
        return isLanguageSupported(companyProfile, languageCode);
    };

    const getUpgradeMsg = (featureName) => {
        return getUpgradeMessage(featureName);
    };

    return {
        currentPlan,
        isPremium,
        isStarter,
        isTrial,
        features,
        limits,
        canAddMenuItem,
        canAddTable,
        canAddBranch,
        canUseLanguage,
        getUpgradeMsg,
    };
};
